import React from "react";
import { MapPin, Phone, Smartphone, Mail } from "lucide-react";
import { CONTACT } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";

export default function Contact() {
  const { t } = useT();
  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="relative py-24 lg:py-32 bg-[var(--hupla-cream)] overflow-hidden"
    >
      <img
        aria-hidden
        src="/hupla-emblem.svg"
        alt=""
        className="pointer-events-none select-none absolute -right-40 top-1/2 -translate-y-1/2 w-[720px] max-w-none opacity-[0.05]"
      />
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="overline text-[#128999]">{t("contact.overline")}</p>
            <h2 className="font-display font-black mt-6 leading-[0.9] text-[clamp(2.25rem,5vw,4.5rem)]">
              {t("contact.titleA")}
              <br />
              <span className="font-editorial text-[#128999]">{t("contact.titleCity")}</span>{" "}
              {t("contact.titleB")}
            </h2>
            <p className="mt-6 text-lg text-neutral-700 leading-relaxed max-w-lg">
              {t("contact.subtitle")}
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            <ContactCard
              icon={MapPin}
              label={t("contact.headquarters")}
              lines={[
                "Büyükdere Cd. 175/6",
                "Ferko Signature",
                "Esentepe, Şişli 34394",
                "İstanbul, Türkiye",
              ]}
            />
            <ContactCard
              icon={Phone}
              label={t("contact.office")}
              lines={["+90 850 302 40 60"]}
              href="tel:+908503024060"
            />
            <ContactCard
              icon={Smartphone}
              label={t("contact.mobile")}
              lines={["+90 532 284 42 83"]}
              href="tel:+905322844283"
            />
            <ContactCard
              icon={Mail}
              label={t("contact.email")}
              lines={["hello@hupla.world"]}
              href="mailto:hello@hupla.world"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactCard({ icon: Icon, label, lines, href }) {
  const inner = (
    <>
      <div className="relative w-14 h-14 flex items-center justify-center">
        {/* Hupla emblem as decorative badge frame */}
        <svg
          viewBox="0 0 74.27 75.18"
          fill="#2CBFCF"
          className="absolute inset-0 w-full h-full drop-shadow-[0_4px_10px_rgba(44,191,207,0.35)]"
          aria-hidden
        >
          <path d="M69.09 27.07l-6.28-4.81V13.2C62.8 5.92 56.89 0 49.61 0c-1.49 0-2.96.25-4.37.75l-8.09 2.83L29.06.75C27.64.25 26.17 0 24.69 0a13.12 13.12 0 0 0-9.22 3.76c-2.57 2.5-3.98 5.86-3.98 9.45v9.06L5.18 27.1C1.88 29.62 0 33.44 0 37.59s1.9 7.97 5.2 10.49l6.29 4.8v8.83c0 7.28 5.92 13.2 13.19 13.2 1.49 0 2.96-.25 4.37-.75l8.09-2.83 8.09 2.83c1.41.49 2.88.75 4.37.75 7.28 0 13.19-5.92 13.19-13.2v-8.94l6.19-4.65a13.1 13.1 0 0 0 5.27-10.49 13.08 13.08 0 0 0-5.18-10.54z" />
        </svg>
        <Icon className="relative w-7 h-7 text-white" strokeWidth={2.5} />
      </div>
      <div className="mt-5">
        <p className="overline text-neutral-500">{label}</p>
        <div className="mt-2 text-lg font-medium text-neutral-900 leading-snug">
          {lines.map((l) => (
            <div key={l}>{l}</div>
          ))}
        </div>
      </div>
    </>
  );

  const cls =
    "block p-8 rounded-3xl bg-white border border-black/10 hover:border-[#2CBFCF] hover:-translate-y-1 transition-all duration-300 h-full";
  return href ? (
    <a href={href} className={cls}>
      {inner}
    </a>
  ) : (
    <div className={cls}>{inner}</div>
  );
}
